import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import SessionExerciseSetsEditor from './SessionExerciseSetsEditor';
import { getApiUrl } from '../utils/apiConfig';
import { hydrateExercisesFromSessionNotes } from '../utils/trainingSessionNotes';
import {
  buildSessionExerciseTypesFromRows,
  isTimeBasedExercise,
  isHeightBasedExercise,
} from '../utils/sessionTimelineMapping';

function typeKey(id) {
  return String(id);
}

function labelInitials(text, len = 2) {
  return String(text || '?').trim().substring(0, len).toUpperCase() || '?';
}

export default function TrainingSessionTimelineView({
  trainingSessionId,
  studentId,
  sessionNotes = '',
  durationMinutes = null,
  readOnly = true,
  initialExpanded = true,
  showNotes = true,
  showSectionTitle = true,
}) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [loading, setLoading] = useState(true);
  const [sessionExerciseTypes, setSessionExerciseTypes] = useState([]);
  const [expandedExerciseTypes, setExpandedExerciseTypes] = useState(new Set());

  const loadTimeline = useCallback(async () => {
    if (!trainingSessionId) {
      setSessionExerciseTypes([]);
      setExpandedExerciseTypes(new Set());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/training-session-exercises?training_session_id=${encodeURIComponent(String(trainingSessionId))}`
      );
      if (!res.ok) {
        setSessionExerciseTypes([]);
        setExpandedExerciseTypes(new Set());
        return;
      }
      const rows = await res.json();
      let list = Array.isArray(rows) ? rows : [];
      list = await hydrateExercisesFromSessionNotes(list, sessionNotes, trainingSessionId);
      const types = buildSessionExerciseTypesFromRows(list);
      setSessionExerciseTypes(types);
      if (initialExpanded) {
        setExpandedExerciseTypes(new Set(types.map((t) => typeKey(t.exercise_type_id))));
      } else {
        setExpandedExerciseTypes(new Set());
      }
    } catch (err) {
      console.error('TrainingSessionTimelineView load error:', err);
      setSessionExerciseTypes([]);
      setExpandedExerciseTypes(new Set());
    } finally {
      setLoading(false);
    }
  }, [trainingSessionId, sessionNotes, initialExpanded]);

  useEffect(() => {
    void loadTimeline();
  }, [loadTimeline]);

  const toggleType = (typeId) => {
    if (readOnly) return;
    const key = typeKey(typeId);
    setExpandedExerciseTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const notesText = sessionNotes != null ? String(sessionNotes).trim() : '';
  const hasDuration =
    durationMinutes != null && durationMinutes !== '' && !Number.isNaN(Number(durationMinutes));

  if (loading) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator color={theme.colors.primary || '#d4af37'} />
        <Text style={styles.loadingText}>Loading session exercises…</Text>
      </View>
    );
  }

  return (
    <View
      style={styles.timelineContainer}
      {...(Platform.OS === 'web' ? { dataSet: { sessionTimeline: 'true' } } : {})}
    >
      {showSectionTitle ? (
        <Text style={styles.timelineSectionTitle}>Training session exercises</Text>
      ) : null}

      {sessionExerciseTypes.length === 0 ? (
        <Text style={styles.timelineEmptyText}>No exercises logged for this session.</Text>
      ) : (
        sessionExerciseTypes.map((exerciseType, index) => {
          const typeId = typeKey(exerciseType.exercise_type_id);
          const isExpanded = readOnly || expandedExerciseTypes.has(typeId);
          const typeName = exerciseType.exercise_type_name || 'Exercise';
          const exercises = Array.isArray(exerciseType.exercises) ? exerciseType.exercises : [];

          return (
            <View
              key={exerciseType.id || typeId || index}
              style={styles.timelineItem}
              {...(Platform.OS === 'web' ? { dataSet: { sessionTimelineItem: 'true' } } : {})}
            >
              <View
                style={styles.exerciseTypeRow}
                {...(Platform.OS === 'web' ? { dataSet: { sessionTimelineTypeRow: 'true' } } : {})}
              >
                <TouchableOpacity
                  style={styles.exerciseTypeContainer}
                  onPress={() => toggleType(exerciseType.exercise_type_id)}
                  activeOpacity={readOnly ? 1 : 0.7}
                  disabled={readOnly}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { sessionTimelineTypeCard: 'true' } }
                    : {})}
                >
                  <View style={styles.exerciseTypeIconContainer}>
                    <View style={styles.exerciseTypeIcon}>
                      <Text style={styles.exerciseTypeIconText}>{labelInitials(typeName)}</Text>
                    </View>
                    <Text style={styles.exerciseTypeName}>{typeName}</Text>
                  </View>
                  {!readOnly ? (
                    <Text style={styles.expandArrow}>{isExpanded ? '▼' : '▶'}</Text>
                  ) : (
                    <Text style={styles.expandArrow}>▼</Text>
                  )}
                </TouchableOpacity>
              </View>

              {isExpanded ? (
                <View
                  style={styles.exercisesDropdown}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { sessionTimelineDropdown: 'true' } }
                    : {})}
                >
                  {exercises.map((exercise, exIndex) => (
                    <View
                      key={exercise.id || `${typeId}-${exIndex}`}
                      style={styles.timelineExerciseItem}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { sessionTimelineExerciseItem: 'true' } }
                        : {})}
                    >
                      <View style={styles.timelineExerciseHeader}>
                        <View style={styles.timelineExerciseIcon}>
                          <Text style={styles.timelineExerciseIconText}>
                            {labelInitials(exercise.exercise_name)}
                          </Text>
                        </View>
                        <View style={styles.timelineExerciseInfo}>
                          <Text style={styles.timelineExerciseName}>
                            {exercise.exercise_name || 'Exercise'}
                          </Text>
                          <Text style={styles.timelineExerciseDetails}>
                            {[
                              `${exercise.sets ?? 0} sets`,
                              isTimeBasedExercise(exercise)
                                ? `${exercise.reps ?? 0} reps · ${exercise.time ?? exercise.reps ?? 0}s`
                                : `${exercise.reps ?? 0} reps`,
                              isHeightBasedExercise(exercise) ? 'height' : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Text>
                        </View>
                      </View>
                      <SessionExerciseSetsEditor
                        exercise={exercise}
                        theme={theme}
                        studentId={studentId}
                        readOnly={readOnly}
                        expandAllReps={readOnly}
                        onExerciseChange={() => {}}
                      />
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          );
        })
      )}

      {showNotes && (notesText || hasDuration) ? (
        <View
          style={styles.advancedSection}
          {...(Platform.OS === 'web' ? { dataSet: { sessionAdvanced: 'true' } } : {})}
        >
          <View style={styles.advancedForm}>
            {notesText ? (
              <>
                <Text style={styles.label}>Notes</Text>
                <View style={styles.notesReadOnlyBox}>
                  <Text style={styles.notesReadOnlyText}>{notesText}</Text>
                </View>
              </>
            ) : null}
            {hasDuration ? (
              <>
                <Text style={styles.label}>Duration</Text>
                <Text style={styles.durationReadOnly}>{durationMinutes} min</Text>
              </>
            ) : null}
          </View>
        </View>
      ) : null}
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    timelineContainer: {
      marginBottom: 4,
      padding: 12,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#f9ffd6',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      ...(Platform.OS === 'web' && !theme.isDarkMode
        ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }
        : {}),
    },
    timelineSectionTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    timelineEmptyText: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.8,
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    loadingWrap: {
      paddingVertical: 24,
      alignItems: 'center',
    },
    loadingText: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.8,
      marginTop: 8,
      backgroundColor: 'transparent',
    },
    timelineItem: { marginBottom: 10, backgroundColor: 'transparent' },
    exerciseTypeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    exerciseTypeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flex: 1,
      padding: 10,
      backgroundColor: theme.isDarkMode ? theme.colors.background : theme.colors.surface,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      ...(Platform.OS === 'web' && !theme.isDarkMode
        ? { overflow: 'hidden', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)' }
        : {}),
    },
    exerciseTypeIconContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      backgroundColor: 'transparent',
    },
    exerciseTypeIcon: {
      width: 50,
      height: 50,
      borderRadius: 25,
      backgroundColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    exerciseTypeIconText: {
      color: '#FFFFFF',
      fontWeight: 'bold',
      fontSize: 14,
    },
    exerciseTypeName: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      flex: 1,
      backgroundColor: 'transparent',
    },
    expandArrow: {
      fontSize: 16,
      color: theme.colors.text,
      marginLeft: 10,
      backgroundColor: 'transparent',
    },
    exercisesDropdown: {
      marginTop: 5,
      marginLeft: 20,
      paddingLeft: 20,
      borderLeftWidth: 2,
      borderLeftColor: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    timelineExerciseItem: {
      padding: 8,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F0F0F0',
      borderRadius: 6,
      borderWidth: theme.isDarkMode ? 0 : 1,
      borderColor: theme.isDarkMode ? 'transparent' : '#E8E8E8',
    },
    timelineExerciseHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    timelineExerciseIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
    },
    timelineExerciseIconText: {
      color: '#FFFFFF',
      fontWeight: 'bold',
      fontSize: 12,
    },
    timelineExerciseInfo: { flex: 1, backgroundColor: 'transparent' },
    timelineExerciseName: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    timelineExerciseDetails: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginTop: 2,
      backgroundColor: 'transparent',
    },
    advancedSection: {
      marginTop: 16,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#f9ffd6',
      overflow: 'hidden',
      ...(theme.isDarkMode
        ? {
            borderTopWidth: 1,
            borderTopColor: theme.colors.border,
          }
        : {
            borderWidth: 1,
            borderColor: theme.colors.border,
            borderRadius: 8,
            ...(Platform.OS === 'web'
              ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }
              : {}),
          }),
    },
    advancedForm: {
      padding: 15,
      backgroundColor: 'transparent',
    },
    label: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '600',
      marginBottom: 8,
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    notesReadOnlyBox: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderRadius: 8,
      padding: 12,
      minHeight: 80,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    notesReadOnlyText: {
      color: theme.colors.text,
      fontSize: 14,
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    durationReadOnly: {
      color: theme.colors.text,
      fontSize: 14,
      backgroundColor: 'transparent',
    },
  });
